// Catch-all gate for staff who can't reach the wizard admin tools. Two
// distinct cases land here, with different copy:
//
//   1. No active grant (Phase 2b). The user is staff in the dashboard but a
//      super_admin hasn't ticked "Self-Serve Admin Access" on their row in
//      Settings → Users. Action: contact account manager to flip the toggle.
//
//   2. Grant exists, but no stations match. The grant is on, yet
//      /stations/my-admin returned []. This happens when the user's
//      assigned-station list (org_admin scope, or manager/sales_rep's
//      app_metadata.stations[]) doesn't resolve to any rows in
//      public.stations that have self_service_enabled = true. Most common
//      reason: the wizard catalog hasn't been provisioned for those
//      station names yet. Action: contact account manager to onboard the
//      stations.
//
// Sole CTA: sign out → /admin. The user can't act here themselves either way.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import CtvBuyerAuthShell, {
  CTV_NAVY,
  CTV_CTA_ACCENT,
  CTV_CTA_DEEP,
} from '../../components/self-service/auth/CtvBuyerAuthShell';

const AccessDeniedPage: React.FC = () => {
  const { user, signOut, hasPublisherAdminGrant, myAdminStations } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin', { replace: true });
  };

  // Differentiate "grant missing" vs "grant on but no stations resolved."
  // Default to the grant-missing copy when state is still loading so a
  // brief race doesn't accidentally show the wrong message.
  const grantOn = hasPublisherAdminGrant === true;
  const stationsResolved = myAdminStations !== null;
  const noStations = grantOn && stationsResolved && myAdminStations.length === 0;

  const emailLabel = user?.email ?? 'Your account';

  let title: string;
  let body: string;
  if (noStations) {
    title = 'No stations are set up for your account';
    body = `${emailLabel} has Streamline publisher access enabled, but none of the stations assigned to your account have been onboarded to Streamline yet. Contact your CTVBuyer account manager to provision your stations.`;
  } else {
    title = 'Streamline Publisher Access Required';
    body = `${user?.email ? `${user.email} doesn't have access` : "Your account doesn't have access"} to the Streamline publisher tools yet. Contact your CTVBuyer account manager or station/channel manager to enable this feature.`;
  }

  return (
    <CtvBuyerAuthShell variant="dark">
      <div className="w-full bg-white rounded-[20px] shadow-2xl p-8 text-center">
        <ShieldAlert className="w-10 h-10 text-amber-600 mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2" style={{ color: CTV_NAVY }}>
          {title}
        </h1>
        <p className="text-sm text-slate-600 mb-6">{body}</p>
        <PrimaryButton
          type="button"
          onClick={handleSignOut}
          accentColor={CTV_CTA_ACCENT}
          primaryColor={CTV_CTA_DEEP}
        >
          Sign out
        </PrimaryButton>
      </div>
    </CtvBuyerAuthShell>
  );
};

export default AccessDeniedPage;
